/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Package, Shirt, Users, MapPin, Sparkles, Wrench, Music, Palette, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENT_CONFIG = {
  ART: {
    icon: Palette,
    title: 'Art Department',
    subtitle: 'Props & Set Dressing',
    color: '#9333ea'
  },
  COSTUME_WARDROBE: {
    icon: Shirt,
    title: 'Costume & Wardrobe',
    subtitle: 'Character Wardrobe Tracker',
    color: '#eab308'
  },
  CASTING: {
    icon: Users,
    title: 'Casting',
    subtitle: 'Character List & Scenes',
    color: '#3b82f6'
  },
  MAKEUP_HAIR: {
    icon: Sparkles,
    title: 'Makeup & Hair',
    subtitle: 'Character Looks & Continuity',
    color: '#ec4899'
  },
  VFX: {
    icon: Eye,
    title: 'VFX',
    subtitle: 'Visual Effects Shots',
    color: '#06b6d4'
  },
  LOCATIONS: {
    icon: MapPin,
    title: 'Locations',
    subtitle: 'Location Breakdown',
    color: '#10b981'
  },
  SFX_STUNTS: {
    icon: Wrench,
    title: 'SFX & Stunts',
    subtitle: 'Special Effects & Stunts',
    color: '#f59e0b'
  },
  SOUND: {
    icon: Music,
    title: 'Sound',
    subtitle: 'Music & Sound Design',
    color: '#14b8a6'
  },
  PRODUCTION: {
    icon: Package,
    title: 'Production',
    subtitle: 'Full Breakdown View',
    color: '#e8c547'
  }
};

const DepartmentDashboard = () => {
  const { projectId, department } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);

  const config = DEPARTMENT_CONFIG[department] || DEPARTMENT_CONFIG.PRODUCTION;
  const DeptIcon = config.icon;

  useEffect(() => {
    fetchDepartmentData();
    if (department === 'CASTING') {
      fetchScenes();
    }
  }, [department]);

  const fetchScenes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/breakdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScenes(response.data);
    } catch (error) {
      console.error('Error fetching scenes:', error);
    }
  };

  const fetchDepartmentData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/department/${department}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load department data');
    } finally {
      setLoading(false);
    }
  };

  const renderCastingView = () => (
    <div className="space-y-8" data-testid="casting-view">
      {/* Character Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.map((character) => (
          <Card key={character.id} className="bg-[#1a1a1a] border-[#2a2a2a] p-6" data-testid={`character-card-${character.name}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="h-12 w-12 rounded-full bg-[#e8c547]/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-[#e8c547]" />
              </div>
              <Badge className="bg-[#4a9eff]/20 text-[#4a9eff]">{character.character_type}</Badge>
            </div>
            <h3 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0] mb-2">{character.name}</h3>
            <p className="text-[#a0a0a0] text-sm mb-3">{character.description}</p>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#a0a0a0]">{character.total_scenes} scenes</span>
              <span className="text-[#4a9eff] truncate ml-2">Scenes: {character.scene_numbers.slice(0, 5).join(', ')}{character.scene_numbers.length > 5 ? '...' : ''}</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Day-Out-of-Days Grid */}
      {scenes.length > 0 && data.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-4 md:p-6" data-testid="dood-grid">
          <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-4">Day-out-of-Days Grid</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2 text-[#a0a0a0] sticky left-0 bg-[#1a1a1a] z-10">Character</th>
                  {scenes.map(s => (
                    <th key={s.id} className="p-2 text-[#a0a0a0] min-w-[40px]" title={s.scene_header}>{formatSceneNumber(s.scene_number)}</th>
                  ))}
                  <th className="p-2 text-[#e8c547]">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.map(char => (
                  <tr key={char.id} className="border-t border-[#2a2a2a]">
                    <td className="p-2 text-[#f0f0f0] font-medium sticky left-0 bg-[#1a1a1a] z-10">{char.name}</td>
                    {scenes.map(s => {
                      const inScene = s.characters_in_scene?.includes(char.name);
                      return (
                        <td key={s.id} className="p-2 text-center">
                          {inScene ? <span className="text-[#e8c547]">●</span> : <span className="text-[#2a2a2a]">·</span>}
                        </td>
                      );
                    })}
                    <td className="p-2 text-center text-[#e8c547] font-bold">{char.total_scenes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );

  const renderLocationsView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="locations-view">
      {data.map((location, idx) => (
        <Card key={location.location_name || idx} className="bg-[#1a1a1a] border-[#2a2a2a] p-6" data-testid={`location-card-${location.location_name}`}>
          <div className="flex items-start justify-between mb-4">
            <div className="h-12 w-12 rounded-full bg-[#10b981]/10 flex items-center justify-center">
              <MapPin className="h-6 w-6 text-[#10b981]" />
            </div>
            <Badge className={location.location_type === 'INT' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}>
              {location.location_type}
            </Badge>
          </div>
          <h3 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0] mb-3">{location.location_name}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#a0a0a0]">Total Scenes:</span>
              <span className="text-[#f0f0f0] font-medium">{location.scenes.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#a0a0a0]">Day:</span>
              <span className="text-[#f0f0f0]">{location.day_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#a0a0a0]">Night:</span>
              <span className="text-[#f0f0f0]">{location.night_count}</span>
            </div>
            <div className="mt-2 text-[#4a9eff]">
              Scenes: {location.scenes.slice(0, 8).join(', ')}{location.scenes.length > 8 ? '...' : ''}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderItemsView = () => {
    // Group items by description
    const grouped = {};
    data.forEach(item => {
      if (!grouped[item.description]) {
        grouped[item.description] = [];
      }
      grouped[item.description].push(item);
    });

    return (
      <div className="space-y-4" data-testid="items-view">
        {Object.entries(grouped).map(([description, items]) => (
          <Card key={description} className="bg-[#1a1a1a] border-[#2a2a2a] p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-[#f0f0f0] font-medium mb-2">{description}</h3>
                <div className="flex flex-wrap gap-2">
                  {items.map((item, idx) => (
                    item.scene_info && (
                      <Badge key={item.id || idx} variant="outline" className="border-[#2a2a2a] text-[#a0a0a0]">
                        Scene {formatSceneNumber(item.scene_info.scene_number)}
                      </Badge>
                    )
                  ))}
                </div>
                {items[0].notes && (
                  <p className="text-[#4a9eff] text-sm mt-2">Note: {items[0].notes}</p>
                )}
              </div>
              <span className="text-[#e8c547] font-bold ml-4">{items.length}x</span>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderProductionView = () => {
    // Redirect to master breakdown for production
    navigate(`/projects/${projectId}/breakdown`);
    return null;
  };

  const renderContent = () => {
    if (loading) {
      return <div className="text-center py-12 text-[#a0a0a0]">Loading department data...</div>;
    }

    if (data.length === 0) {
      return (
        <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
          <DeptIcon className="h-16 w-16 text-[#a0a0a0] mx-auto mb-4" />
          <p className="text-[#a0a0a0]">No data available for this department</p>
        </Card>
      );
    }

    if (department === 'CASTING') return renderCastingView();
    if (department === 'LOCATIONS') return renderLocationsView();
    if (department === 'PRODUCTION') return renderProductionView();
    return renderItemsView();
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] p-4 md:p-8 md:pl-8 pl-16" data-testid="department-dashboard-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/projects/${projectId}/breakdown`)}
              className="text-[#a0a0a0] hover:text-[#f0f0f0] mb-2"
              data-testid="back-to-breakdown-btn"
            >
              ← Back to Master Breakdown
            </Button>
            <div className="flex items-center gap-4 mb-2">
              <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>
                <DeptIcon className="h-6 w-6" style={{ color: config.color }} />
              </div>
              <div>
                <h1 className="font-['DM_Serif_Display'] text-4xl text-[#f0f0f0]">{config.title}</h1>
                <p className="text-[#a0a0a0]">{config.subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default DepartmentDashboard;
